import React, { useState, useMemo, useEffect, useRef } from "react";
import moment from "moment";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

export default function CapacityActivityTable({ activities, onEdit }) {
  // Search state
  const [search, setSearch] = useState("");

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Export dropdown open/close state
  const [exportDropdownOpen, setExportDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Filter activities by search query
  const filteredActivities = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return activities;

    return activities.filter((activity) =>
      activity.title.toLowerCase().includes(q) ||
      activity.description.toLowerCase().includes(q) ||
      activity.trainer.toLowerCase().includes(q) ||
      activity.department.toLowerCase().includes(q)
    );
  }, [activities, search]);

  // Reset to page 1 if filtered data changes
  useEffect(() => {
    setCurrentPage(1);
  }, [search, activities]);

  // Paginated data slice
  const paginatedActivities = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredActivities.slice(start, start + itemsPerPage);
  }, [filteredActivities, currentPage]);

  // Calculate total pages
  const totalPages = Math.ceil(filteredActivities.length / itemsPerPage);

  // Map activity data for export
  const mapActivityToExportRow = (activity) => ({
    Title: activity.title,
    Description: activity.description,
    Date: moment(activity.date).format("YYYY-MM-DD"),
    Trainer: activity.trainer,
    Department: activity.department,
  });

  // Export Excel
  const exportToExcel = () => {
    if (!filteredActivities.length) {
      alert("No activities to export!");
      return;
    }
    const exportData = filteredActivities.map(mapActivityToExportRow);
    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Capacity Activities");

    const maxColWidths = Object.keys(exportData[0]).map((col) => {
      const maxLength = Math.max(
        col.length,
        ...exportData.map((row) => (row[col] ? row[col].toString().length : 0))
      );
      return { wch: maxLength + 5 };
    });
    worksheet["!cols"] = maxColWidths;

    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const dataBlob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    saveAs(dataBlob, "capacity_activities_export.xlsx");
  };

  // Export CSV
  const exportToCSV = () => {
    if (!filteredActivities.length) {
      alert("No activities to export!");
      return;
    }
    const exportData = filteredActivities.map(mapActivityToExportRow);
    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const csv = XLSX.utils.sheet_to_csv(worksheet);

    const csvBlob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    saveAs(csvBlob, "capacity_activities_export.csv");
  };

  // Export PDF
  const exportToPDF = () => {
    if (!filteredActivities.length) {
      alert("No activities to export!");
      return;
    }

    const exportData = filteredActivities.map(mapActivityToExportRow);
    const doc = new jsPDF("l", "pt", "a4");

    const columns = Object.keys(exportData[0]);

    doc.setFontSize(18);
    doc.setTextColor(237, 125, 49);
    doc.text("Capacity Activities Export", 40, 40);

    autoTable(doc, {
      startY: 60,
      head: [columns],
      body: exportData.map((row) => columns.map((col) => row[col])),
      styles: { fontSize: 9, cellPadding: 4 },
      headStyles: { fillColor: [237, 125, 49] },
      theme: "striped",
      margin: { left: 20, right: 20 },
      didDrawPage: (data) => {
        const pageCount = doc.internal.getNumberOfPages();
        doc.setFontSize(10);
        doc.setTextColor(150);
        doc.text(
          `Page ${doc.internal.getCurrentPageInfo().pageNumber} of ${pageCount}`,
          data.settings.margin.left,
          doc.internal.pageSize.height - 10
        );
      },
    });

    doc.save("capacity_activities_export.pdf");
  };

  // Export button handler
  const handleExport = (format) => {
    setExportDropdownOpen(false);
    switch (format) {
      case "excel":
        exportToExcel();
        break;
      case "csv":
        exportToCSV();
        break;
      case "pdf":
        exportToPDF();
        break;
      default:
        alert("Unsupported export format");
    }
  };

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setExportDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="mt-8 bg-white rounded shadow p-4">
      <h2 className="text-lg font-semibold text-gray-700 mb-4">
        Capacity Activities
      </h2>

      {/* Search and Export controls */}
      <div className="flex flex-col sm:flex-row justify-between items-center mb-4 gap-3">
        {/* Search input */}
        <input
          type="search"
          placeholder="Search activities by title, trainer or department"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full sm:max-w-xs rounded border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500"
          aria-label="Search capacity activities"
        />

        {/* Export dropdown */}
        <div className="relative inline-block text-left" ref={dropdownRef}>
          <button
            onClick={() => setExportDropdownOpen((open) => !open)}
            type="button"
            className="inline-flex justify-center w-full rounded bg-orange-600 px-4 py-2 text-white hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500"
            aria-haspopup="true"
            aria-expanded={exportDropdownOpen}
            aria-controls="export-menu"
          >
            Export
            <svg
              className="ml-2 -mr-1 h-5 w-5"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
              aria-hidden="true"
            >
              <path
                fillRule="evenodd"
                d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.25a.75.75 0 01-1.06 0L5.21 8.27a.75.75 0 01.02-1.06z"
                clipRule="evenodd"
              />
            </svg>
          </button>

          {/* Dropdown menu */}
          {exportDropdownOpen && (
            <div
              id="export-menu"
              className="origin-top-right absolute right-0 mt-2 w-48 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-50"
              role="menu"
              aria-orientation="vertical"
              aria-labelledby="export-menu-button"
              tabIndex={-1}
            >
              <div className="py-1">
                {["excel", "csv", "pdf"].map((format) => (
                  <button
                    key={format}
                    onClick={() => handleExport(format)}
                    className="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-orange-100 hover:text-orange-900"
                    role="menuitem"
                  >
                    Export as {format.toUpperCase()}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="bg-orange-100 text-gray-600 text-left">
              <th className="px-4 text-orange-600 py-2">No</th>
              <th className="px-4 text-orange-600 py-2">Title</th>
              <th className="px-4 text-orange-600 py-2">Description</th>
              <th className="px-4 text-orange-600 py-2">Date</th>
              <th className="px-4 text-orange-600 py-2">Trainer</th>
              <th className="px-4 text-orange-600 py-2">Department</th>
              <th className="px-4 text-orange-600 py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {paginatedActivities.length > 0 ? (
              paginatedActivities.map((activity, index) => (
                <tr key={activity._id} className="border-b hover:bg-gray-50">
                  <td className="px-4 py-2">{(currentPage - 1) * itemsPerPage + index + 1}</td>
                  <td className="px-4 py-2">{activity.title}</td>
                  <td className="px-4 py-2">{activity.description}</td>
                  <td className="px-4 py-2">{moment(activity.date).format("YYYY-MM-DD")}</td>
                  <td className="px-4 py-2">{activity.trainer}</td>
                  <td className="px-4 py-2">{activity.department}</td>
                  <td className="px-4 py-2">
                    <button
                      onClick={() => onEdit(activity)}
                      className="text-orange-600 hover:underline"
                    >
                      Edit
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="7" className="text-center py-4 text-gray-500">
                  No capacity activities found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="mt-4 flex justify-center gap-2">
          <button
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            className={`rounded px-3 py-1 ${currentPage === 1
              ? "cursor-not-allowed bg-gray-300"
              : "bg-orange-100 text-orange-600 hover:bg-orange-200"
              }`}
          >
            Prev
          </button>

          {[...Array(totalPages)].map((_, i) => (
            <button
              key={i}
              onClick={() => setCurrentPage(i + 1)}
              className={`rounded px-3 py-1 ${currentPage === i + 1
                ? "bg-orange-300 text-orange-900 font-semibold"
                : "bg-gray-200 hover:bg-gray-300"
                }`}
            >
              {i + 1}
            </button>
          ))}

          <button
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            className={`rounded px-3 py-1 ${currentPage === totalPages
              ? "cursor-not-allowed bg-gray-300"
              : "bg-orange-100 text-orange-600 hover:bg-orange-200"
              }`}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}